"use client";

import React, { useRef, useEffect } from "react";

interface AnimatedThunderRainProps {
  /** Color for the cloud's outer shape */
  cloudColor?: string;
  /** Color for the cloud's internal shape */
  cloudInnerColor?: string;
  /** Color for the lightning bolt */
  thunderColor?: string;
  /** Color for the raindrops */
  rainColor?: string;
  /** Canvas size in pixels (width & height). */
  size?: number;
  /** Additional CSS classes. */
  className?: string;
  /** How many raindrops to draw at once. */
  dropCount?: number;
  /** Speed multiplier for the animation. */
  speed?: number;
}

const AnimatedThunderRain: React.FC<AnimatedThunderRainProps> = ({
  cloudColor = "#444444",
  cloudInnerColor = "#333333",
  thunderColor = "#00BFFF", // Changed to blue lightning color
  rainColor = "#87CEEB",
  size = 64,
  className = "",
  dropCount = 3,
  speed = 1.0,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const requestRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const TAU = 2 * Math.PI;
  const STROKE = 0.08;

  /**
   * Draws a filled circle.
   */
  function circle(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    r: number
  ) {
    ctx.beginPath();
    ctx.arc(x, y, r, 0, TAU, false);
    ctx.fill();
  }

  /**
   * Draws a set of "puffs" that together look like a cloud shape.
   */
  function puffs(
    ctx: CanvasRenderingContext2D,
    t: number,
    cx: number,
    cy: number,
    rx: number,
    ry: number,
    rmin: number,
    rmax: number
  ) {
    function puff(
      ctx: CanvasRenderingContext2D,
      t: number,
      cx: number,
      cy: number,
      rx: number,
      ry: number,
      rmin: number,
      rmax: number
    ) {
      const c = Math.cos(t * TAU);
      const s = Math.sin(t * TAU);
      const rm = rmax - rmin;

      circle(
        ctx,
        cx - s * rx,
        cy + c * ry + rm * 0.5,
        rmin + (1 - c * 0.5) * rm
      );
    }

    for (let i = 0; i < 5; i++) {
      puff(ctx, t + i / 5, cx, cy, rx, ry, rmin, rmax);
    }
  }

  /**
   * Draws a two-tone cloud: an outer color + an inner color.
   */
  function cloud(
    ctx: CanvasRenderingContext2D,
    t: number,
    cx: number,
    cy: number,
    cw: number,
    s: number,
    outerColor: string,
    innerColor: string
  ) {
    // slow drift
    t /= 30000;

    const a = cw * 0.21;
    const b = cw * 0.12;
    const c = cw * 0.24;
    const d = cw * 0.28;

    // --- 1) Draw the outer cloud shape ---
    ctx.fillStyle = outerColor;
    puffs(ctx, t, cx, cy, a, b, c, d);

    // --- 2) Carve out a smaller region in the center to make room for another color ---
    ctx.save();
    ctx.globalCompositeOperation = "destination-out";
    puffs(ctx, t, cx, cy, a, b, c - s, d - s);
    ctx.restore();

    // --- 3) Fill that smaller region with the 'innerColor' ---
    ctx.fillStyle = innerColor;
    puffs(ctx, t, cx, cy, a, b, c - s, d - s);
  }

  /**
   * Draws raindrops falling from the cloud.
   */
  function rain(
    ctx: CanvasRenderingContext2D,
    t: number,
    cx: number,
    cy: number,
    cw: number,
    s: number,
    dropColor: string,
    count: number,
    speedMultiplier: number
  ) {
    // 2000ms per cycle, scaled by speed
    t /= (2000 / speedMultiplier);

    const xSpread = cw * 0.16;
    const arcStart = TAU * (11 / 12);
    const arcEnd = TAU * (7 / 12);

    for (let i = 0; i < count; i++) {
      const p = (t + i / count) % 1;
      const x = cx + ((i - count/2 + 0.5) / (count/2)) * xSpread;
      const y = cy + p * p * cw;

      ctx.fillStyle = dropColor;
      ctx.beginPath();
      ctx.moveTo(x, y - s * 1.5);
      ctx.arc(x, y, s * 0.75, arcStart, arcEnd, false);
      ctx.fill();
    }
  }

  /**
   * Draws improved lightning bolts with a blue glow effect.
   */
  function thunderbolts(
    ctx: CanvasRenderingContext2D,
    t: number,
    cx: number,
    cy: number,
    cw: number,
    color: string,
    speedVal: number
  ) {
    // Use a faster cycle for thunder (500ms instead of 1000ms)
    const thunderSpeed = 500 / speedVal;
    t = t % (thunderSpeed * 15); // Reset every 15 cycles to create pauses between storms

    // Only show lightning during certain time windows
    if (t < thunderSpeed * 4) {
      // Flash frequency - create multiple quick flashes
      const flashPhase = (t % thunderSpeed) / thunderSpeed;

      // Make lightning more visible with higher alpha
      let alpha = 0.9;

      // Create a natural flash effect - brightest in middle of flash
      if (flashPhase < 0.5) {
        alpha = flashPhase * 1.8; // Fade in (faster)
      } else {
        alpha = (1 - flashPhase) * 1.8; // Fade out (faster)
      }

      // Size of the lightning bolt
      const boltSize = cw / 18;

      // Calculate positions for multiple bolts
      const boltPattern = Math.floor((t / thunderSpeed) % 3);
      const offsets = [-0.18, 0, 0.18]; // Left, center, right positions

      // Use the pattern to determine which lightning bolt(s) to show
      const xOffset = offsets[boltPattern] * cw;
      const x = cx + xOffset;
      const y = cy + cw * 0.13; // Start just below the cloud

      // Draw the glow effect first (larger, more transparent)
      ctx.fillStyle = color;
      ctx.globalAlpha = alpha * 0.4;

      // Draw a larger glow version of the bolt
      drawLightningBolt(ctx, x, y, boltSize * 1.4);

      // Draw the main lightning bolt (smaller, more opaque)
      ctx.globalAlpha = alpha;
      drawLightningBolt(ctx, x, y, boltSize);

      // Reset the global alpha
      ctx.globalAlpha = 1.0;
    }
  }

  /**
   * Helper function to draw a single lightning bolt with a realistic jagged pattern
   */
  function drawLightningBolt(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    boltSize: number
  ) {
    // Draw a more realistic zigzag lightning bolt
    ctx.beginPath();

    // Starting point
    ctx.moveTo(x, y);

    // Add some randomization to make each bolt look a bit different
    const jitter = Math.random() * 0.2 - 0.1;

    // First segment - left branch
    ctx.lineTo(x - boltSize * (0.5 + jitter), y + boltSize * 2);

    // Middle segment - back right
    ctx.lineTo(x + boltSize * (0.3 - jitter), y + boltSize * 3.5);

    // Lower segment - back left
    ctx.lineTo(x - boltSize * (0.2 + jitter), y + boltSize * 5);

    // Tip of the bolt - right
    ctx.lineTo(x + boltSize * (0.8 - jitter), y + boltSize * 6.5);

    // Connect back to create branches
    ctx.lineTo(x - boltSize * (0.2 + jitter), y + boltSize * 3.5);
    ctx.lineTo(x + boltSize * (0.5 - jitter), y + boltSize * 2);
    ctx.lineTo(x, y);

    ctx.closePath();
    ctx.fill();

    // Add small branches (optional)
    if (Math.random() > 0.5) {
      // Small branch 1
      ctx.beginPath();
      ctx.moveTo(x - boltSize * 0.2, y + boltSize * 3);
      ctx.lineTo(x - boltSize * 1.2, y + boltSize * 3.5);
      ctx.lineTo(x - boltSize * 0.5, y + boltSize * 3.2);
      ctx.closePath();
      ctx.fill();
    }
  }

  /**
   * Clears the canvas and draws one frame of the animation.
   */
  function drawFrame(ctx: CanvasRenderingContext2D, elapsed: number) {
    const w = ctx.canvas.width;
    const h = ctx.canvas.height;
    const s = Math.min(w, h);

    // Clear canvas
    ctx.clearRect(0, 0, w, h);

    // 1) Draw raindrops first
    rain(ctx, elapsed, w * 0.5, h * 0.5, s * 0.9, s * STROKE, rainColor, dropCount, speed);

    // 2) Draw lightning bolts with blue color and glow effect
    thunderbolts(ctx, elapsed, w * 0.5, h * 0.4, s * 0.9, thunderColor, speed);

    // 3) Draw the two-tone cloud on top
    cloud(ctx, elapsed, w * 0.5, h * 0.37, s * 0.9, s * STROKE, cloudColor, cloudInnerColor);
  }

  /**
   * Main animation loop using requestAnimationFrame.
   */
  const animate = (timestamp: number) => {
    if (startTimeRef.current === null) {
      startTimeRef.current = timestamp;
    }
    const elapsed = timestamp - startTimeRef.current;

    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        drawFrame(ctx, elapsed);
      }
    }
    requestRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate);
    return () => {
      if (requestRef.current !== null) {
        cancelAnimationFrame(requestRef.current);
      }
    };
    // Re-run if any props change
  }, [cloudColor, cloudInnerColor, thunderColor, rainColor, size, dropCount, speed]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      width={size}
      height={size}
      style={{ width: size, height: size }}
    />
  );
};

export default AnimatedThunderRain;